import React, { useEffect, useRef, useState } from 'react';
import { Ban } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { puedeExcepcionProhibido } from '../../lib/roles';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

/**
 * PACIENTE PROHIBIDO AL DAR CITA (administración, 14/10/2026)
 * -----------------------------------------------------------
 * Si se intenta dar una cita (o pasar una cita) a un paciente marcado como
 * prohibido, salta un aviso emergente —SIN el motivo— y la cita no se da: el
 * servidor contesta 409 de todas formas.
 *
 * La excepción es solo de admin y dirección (`puedeExcepcionProhibido`), y
 * pide una confirmación de más: la casilla «Excepción autorizada: dar la cita
 * igualmente». Con ella marcada, la pantalla manda `excepcion_prohibido:
 * true` y el servidor la apunta en Control rastreo con quién y cuándo.
 *
 * Tres piezas, para los tres sitios que dan o pasan citas (DarCitaDialog,
 * CitaCreateDialog y el cambio de paciente del resumen de la cita):
 *  - `useAvisoProhibido(prohibido, clave)`: el estado del aviso y de la
 *    casilla, que se reinician al cambiar de paciente (`clave`).
 *  - `<DialogoPacienteProhibido>`: el aviso emergente.
 *  - `<AvisoProhibidoEnCita>`: el recuadro rojo que se queda en el diálogo.
 */

export const MENSAJE_PROHIBIDO = 'Este paciente está marcado como prohibido. No se le pueden dar citas.';
export const CASILLA_EXCEPCION = 'Excepción autorizada: dar la cita igualmente';

/** ¿Es el 409 de un paciente prohibido? */
export function esErrorProhibido(e) {
  return e?.response?.status === 409 && e?.response?.data?.prohibido === true;
}

export function useAvisoProhibido(prohibido, clave) {
  const { user } = useAuth();
  const puedeExcepcion = puedeExcepcionProhibido(user);
  const [excepcion, setExcepcion] = useState(false);
  const [avisoAbierto, setAvisoAbierto] = useState(false);
  const avisado = useRef(null);

  // Otro paciente: la casilla vuelve a estar sin marcar.
  useEffect(() => {
    setExcepcion(false);
    avisado.current = null;
  }, [clave]);

  // El aviso emergente, una vez por paciente, en cuanto se sabe que lo está.
  useEffect(() => {
    if (prohibido && clave && avisado.current !== clave) {
      avisado.current = clave;
      setAvisoAbierto(true);
    }
  }, [prohibido, clave]);

  return {
    puedeExcepcion,
    excepcion: prohibido && puedeExcepcion && excepcion,
    setExcepcion,
    avisoAbierto,
    setAvisoAbierto,
    // Un paciente prohibido solo se cita con la excepción marcada.
    bloquea: !!prohibido && !(puedeExcepcion && excepcion),
  };
}

export function DialogoPacienteProhibido({
  open, onOpenChange, puedeExcepcion, onExcepcion, testId = 'aviso-prohibido',
}) {
  const [marcada, setMarcada] = useState(false);
  useEffect(() => { if (open) setMarcada(false); }, [open]);
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-700">
            <Ban className="w-5 h-5" /> Paciente prohibido
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#13231E]" data-testid={`${testId}-texto`}>
            {MENSAJE_PROHIBIDO}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {puedeExcepcion && (
          <label className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-sm text-red-900">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={marcada}
              onChange={(e) => setMarcada(e.target.checked)}
              data-testid={`${testId}-casilla`}
            />
            <span>
              <span className="font-semibold">{CASILLA_EXCEPCION}</span>
              <span className="block text-xs text-red-800/80">
                Solo administración y dirección. Queda en Control rastreo quién la autoriza y cuándo.
              </span>
            </span>
          </label>
        )}
        <AlertDialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cerrar`}>
            {puedeExcepcion ? 'No dar la cita' : 'Entendido'}
          </Button>
          {puedeExcepcion && (
            <Button
              type="button"
              disabled={!marcada}
              onClick={() => { onExcepcion?.(); onOpenChange(false); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`${testId}-excepcion`}
            >
              Continuar con la excepción
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** El recuadro rojo dentro del diálogo de la cita, con la casilla para
 *  admin y dirección. */
export function AvisoProhibidoEnCita({ puedeExcepcion, excepcion, onExcepcion, testId = 'cita-prohibido' }) {
  return (
    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid={testId} role="alert">
      <div className="flex items-center gap-2 font-semibold">
        <span className="inline-flex items-center rounded bg-red-600 px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide text-white">
          Prohibido
        </span>
        {MENSAJE_PROHIBIDO}
      </div>
      {puedeExcepcion ? (
        <label className="mt-1.5 flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!excepcion}
            onChange={(e) => onExcepcion?.(e.target.checked)}
            data-testid={`${testId}-casilla`}
          />
          <span className="font-semibold">{CASILLA_EXCEPCION}</span>
        </label>
      ) : (
        <div className="mt-1 text-xs text-red-800/80">
          Solo administración y dirección pueden autorizar una excepción.
        </div>
      )}
    </div>
  );
}
